import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import { statusBadgeClass } from "../utils/statusBadge";

// Status-entry events that change a subtask's state (the rest are shown in the timeline only).
const STATE_OF_EVENT = { assigned: "running", submitted: "in_review", sent_back: "sent_back", accepted: "accepted",
  failed: "failed", dropped: "dropped", cap_reached: "in_review" };

/** What a board row shows: a review is its verdict plus feedback, everything else its text/detail. */
function rowText(row, t) {
  const body = row.body || {};
  if (row.kind === "review" && body.verdict) {
    const verdict = t(`agents.teamVerdict_${body.verdict}`, { defaultValue: body.verdict });
    return body.feedback ? `${verdict}: ${body.feedback}` : verdict;
  }
  return typeof body.text === "string" ? body.text : body.detail;
}

/** The live picture of a team run (agent-teams spec §7): the plan with each subtask's status and
 * the board timeline (every message between the agents). Reloads only new entries whenever
 * `version` (the run's latest team event seq) changes. */
export default function TeamPanel({ runId, version }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const lastSeq = useRef(0);

  useEffect(() => { lastSeq.current = 0; setRows([]); }, [runId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        let items = [];
        let cursor = null;
        do {
          const query = cursor ? `cursor=${encodeURIComponent(cursor)}` : `after=${lastSeq.current}`;
          const page = await apiFetchJson(`/api/v1/agents/runs/${encodeURIComponent(runId)}/board?${query}`);
          items = items.concat(page.items || []);
          cursor = page.next_cursor;
        } while (cursor && !cancelled);
        if (cancelled || !items.length) return;
        lastSeq.current = items[items.length - 1].seq;
        setRows((prev) => [...prev, ...items]);
        setError("");
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [runId, version]);

  const subtasks = useMemo(() => {
    const plans = rows.filter((r) => r.kind === "plan" && r.body && r.body.subtasks);
    const latest = plans.length ? plans[plans.length - 1].body.subtasks : [];
    return latest.map((s) => {
      const moves = rows.filter((r) => r.kind === "status" && r.subtask_id === s.id && r.body && STATE_OF_EVENT[r.body.event]);
      const last = moves.length ? STATE_OF_EVENT[moves[moves.length - 1].body.event] : "waiting";
      const sentBack = moves.filter((r) => r.body.event === "sent_back").length;
      return { ...s, state: last, sentBack };
    });
  }, [rows]);

  const timeline = rows.filter((r) => r.kind !== "plan");
  return (
    <div className="ac-detail-section">
      <h4 className="ac-detail-heading">{t("agents.teamTitle")}</h4>
      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}
      <ul className="ds-list">
        {subtasks.map((s) => (
          <li key={s.id} className="ds-list-row">
            <span className={statusBadgeClass(s.state)}>{t(`agents.teamState_${s.state}`)}</span>
            <span className="ds-list-title">{s.title}</span>
            {s.sentBack > 0 && <span className="ds-badge ds-badge--warning">{t("agents.teamSentBack", { count: s.sentBack })}</span>}
          </li>
        ))}
      </ul>
      <h5 className="ac-detail-heading">{t("agents.teamBoard")}</h5>
      <ol className="agent-steps">
        {timeline.map((r) => (
          <li key={r.seq}>
            <strong>{r.author}</strong>
            {r.recipient && <> → {r.recipient}</>}
            {" · "}<span className="ds-meta">{t(`agents.teamKind_${r.kind}`, { defaultValue: r.kind })}</span>
            {" · "}<span>{rowText(r, t)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
